import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, message, Typography, Descriptions,
  Input, Modal, Form, Select, InputNumber, Popconfirm, Divider, Alert,
  Statistic, Row, Col,
} from 'antd';
import {
  ApiOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  LinkOutlined, VideoCameraOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, InfoCircleOutlined, KeyOutlined, SearchOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const DEFAULT_MODELS = [
  {
    model_name: 'qd/seedance-2.0',
    display_name: 'Seedance 2.0',
    model_type: 'video',
    upstream_model_name: 'dreamina-seedance-2-0-260128',
    input_price: 7,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/videos',
    status: 'active',
    priority: 10,
    metadata: JSON.stringify({
      version: '2.0', tier: '官转', tierColor: 'blue',
      desc: 'Seedance 2.0，纯 URL 素材，无尾帧',
      features: ['官转', '纯 URL 素材', '480p/720p/1080p/4k', '无尾帧'],
      fpsOptions: [24],
      videoModes: ['full_reference', 'start_end_frames'],
      maxRefAudio: 3,
      resolutions: [
        { label: '480p 标清', value: '480p', disabled: false },
        { label: '720p 高清', value: '720p', disabled: false },
        { label: '1080p 超清', value: '1080p', disabled: false },
        { label: '4K 超清', value: '4k', disabled: false },
      ],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      assetLibrary: false, maxRefImages: 9, maxRefVideos: 3, durationRange: [4, 15],
      resolution_pricing: { '480p': 7.0, '720p': 7.0, '1080p': 7.7, '4k': 4.0 },
      returnLastFrameSupported: false,
      resolution_pricing_with_video: { '480p': 4.3, '720p': 4.3, '1080p': 4.7, '4k': 2.4 },
    }),
  },
  {
    model_name: 'qd/seedance-2.0-fast',
    display_name: 'Seedance 2.0 Fast',
    model_type: 'video',
    upstream_model_name: 'dreamina-seedance-2-0-fast-260128',
    input_price: 5.6,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/videos',
    status: 'active',
    priority: 9,
    metadata: JSON.stringify({
      version: '2.0 Fast', tier: '快速版', tierColor: 'green',
      desc: 'Seedance 2.0 Fast，纯 URL 素材，无尾帧',
      features: ['官转', '快速出片', '480p/720p', '无尾帧'],
      fpsOptions: [24],
      videoModes: ['full_reference', 'start_end_frames'],
      maxRefAudio: 3,
      resolutions: [
        { label: '480p 标清', value: '480p', disabled: false },
        { label: '720p 高清', value: '720p', disabled: false },
      ],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      assetLibrary: false, maxRefImages: 9, maxRefVideos: 3, durationRange: [4, 15],
      resolution_pricing: { '480p': 5.6, '720p': 5.6 },
      returnLastFrameSupported: false,
      resolution_pricing_with_video: { '480p': 3.3, '720p': 3.3 },
    }),
  },
  {
    model_name: 'qd/seedance-2.0-mini',
    display_name: 'Seedance 2.0 Mini',
    model_type: 'video',
    upstream_model_name: 'dreamina-seedance-2-0-mini-260615',
    input_price: 3.5,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/videos',
    status: 'active',
    priority: 8,
    metadata: JSON.stringify({
      version: '2.0 Mini', tier: '轻量版', tierColor: 'orange',
      desc: 'Seedance 2.0 Mini，纯 URL 素材，无尾帧',
      features: ['官转', '轻量', '480p/720p', '无尾帧'],
      fpsOptions: [24],
      videoModes: ['full_reference', 'start_end_frames'],
      maxRefAudio: 3,
      resolutions: [
        { label: '480p 标清', value: '480p', disabled: false },
        { label: '720p 高清', value: '720p', disabled: false },
      ],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      assetLibrary: false, maxRefImages: 9, maxRefVideos: 3, durationRange: [4, 15],
      resolution_pricing: { '480p': 3.5, '720p': 3.5 },
      returnLastFrameSupported: false,
      resolution_pricing_with_video: { '480p': 2.1, '720p': 2.1 },
    }),
  },
  {
    model_name: 'qd/seedance-2.5',
    display_name: 'Seedance 2.5',
    model_type: 'video',
    upstream_model_name: 'dreamina-seedance-2-5-260628',
    input_price: 10.7,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/videos',
    status: 'active',
    priority: 11,
    metadata: JSON.stringify({
      version: '2.5', tier: '旗舰版', tierColor: 'red',
      desc: 'Seedance 2.5，最长 30 秒，纯 URL 素材，无尾帧',
      features: ['官转', '最长30秒', '50个多模态参考', '480p/720p/1080p', '无尾帧'],
      fpsOptions: [24],
      videoModes: ['full_reference', 'start_end_frames'],
      maxRefAudio: 10,
      resolutions: [
        { label: '480p 标清', value: '480p', disabled: false },
        { label: '720p 高清', value: '720p', disabled: false },
        { label: '1080p 超清', value: '1080p', disabled: false },
      ],
      aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', 'adaptive'],
      assetLibrary: false, maxRefImages: 30, maxRefVideos: 10, durationRange: [4, 30],
      resolution_pricing: { '480p': 10.7, '720p': 10.7, '1080p': 11.7 },
      returnLastFrameSupported: false,
      resolution_pricing_with_video: { '480p': 6.4, '720p': 6.4, '1080p': 7.0 },
    }),
  },
];

const parseModelMetadata = (model) => {
  if (!model?.metadata) return null;
  try {
    const meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata;
    return {
      tier: meta.tier || '',
      tierColor: meta.tierColor || 'default',
      desc: meta.desc || '',
      features: meta.features || [],
      resolutionPricing: meta.resolution_pricing || null,
    };
  } catch {
    return { tier: '', tierColor: 'default', desc: '', features: [] };
  }
};

const qdUpstreamFee = (billing) => {
  const amount = billing?.amount_usd ?? billing?.amount_cny ?? billing?.amount;
  if (amount == null) return '-';
  return `$${Number(amount).toFixed(6)} USD`;
};

const Qd = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState(null);
  const [allModels, setAllModels] = useState([]);
  const [models, setModels] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [usageTaskId, setUsageTaskId] = useState('');
  const [taskUsage, setTaskUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [providerForm] = Form.useForm();
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [providerEditMode, setProviderEditMode] = useState(false);

  const [modelForm] = Form.useForm();
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelEditMode, setModelEditMode] = useState(false);
  const [editingModel, setEditingModel] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, modelRes, dashRes] = await Promise.all([
        adminAPI.getProviders(),
        adminAPI.getModelConfigs(),
        adminAPI.getQdDashboard().catch(() => null),
      ]);

      let foundProvider = null;
      if (provRes.code === 0) {
        const providers = provRes.data?.list || provRes.data || [];
        foundProvider = providers.find(p => {
          const name = (p.name || '').toLowerCase();
          return name === 'qd' || name === 'qd-sd' || name.startsWith('qd-') ||
            (p.api_base || '').toLowerCase().includes('qidong.');
        }) || null;
      }
      setProvider(foundProvider);

      if (modelRes.code === 0) {
        const list = modelRes.data?.list || modelRes.data || [];
        setAllModels(list);
        setModels(foundProvider ? list.filter(m => m.provider_id === foundProvider.id) : []);
      }
      setDashboard(dashRes?.code === 0 ? dashRes.data : null);
    } catch (e) {
      console.error('Failed to fetch QD data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateProvider = () => {
    setProviderEditMode(false);
    providerForm.resetFields();
    providerForm.setFieldsValue({
      name: 'qd-sd',
      display_name: 'qd',
      api_base: 'https://qidong.cyou/v1',
      adapter_type: 'openai',
      provider_type: 'internal',
      status: 'active',
      priority: 20,
      is_approved: true,
    });
    setProviderModalOpen(true);
  };

  const openEditProvider = () => {
    if (!provider) return;
    setProviderEditMode(true);
    providerForm.setFieldsValue({
      ...provider,
      priority: provider.priority ?? 20,
      is_approved: provider.is_approved ?? true,
    });
    setProviderModalOpen(true);
  };

  const handleProviderSave = async () => {
    try {
      const values = await providerForm.validateFields();
      setSaving(true);
      const res = providerEditMode && provider
        ? await adminAPI.updateProvider(provider.id, { ...provider, ...values, is_approved: provider.is_approved ?? true })
        : await adminAPI.createProvider({ ...values, is_approved: true });
      if (res.code === 0) {
        message.success(providerEditMode ? 'QD 供应商已更新' : 'QD 供应商已创建');
        setProviderModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('保存失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = async () => {
    if (!provider) return;
    try {
      const res = await adminAPI.deleteProvider(provider.id);
      if (res.code === 0) {
        message.success('供应商已删除');
        setProvider(null);
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  const openCreateModel = () => {
    setModelEditMode(false);
    setEditingModel(null);
    modelForm.resetFields();
    modelForm.setFieldsValue({
      model_type: 'video',
      provider_id: provider?.id || 0,
      status: 'active',
      input_price: 7,
      output_price: 0,
      billing_type: 'per_token',
      billing_rate: 0,
      api_path: '/videos',
      priority: 5,
      metadata: DEFAULT_MODELS[0].metadata,
    });
    setModelModalOpen(true);
  };

  const openEditModel = (model) => {
    setModelEditMode(true);
    setEditingModel(model);
    let metadata = model.metadata || '';
    try {
      const obj = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      metadata = JSON.stringify(obj, null, 2);
    } catch { /* 保留原始文本 */ }
    modelForm.setFieldsValue({
      ...model,
      model_type: model.model_type || model.type || 'video',
      provider_id: model.provider_id || provider?.id || 0,
      metadata,
    });
    setModelModalOpen(true);
  };

  const handleModelSave = async () => {
    try {
      const values = await modelForm.validateFields();
      try {
        JSON.parse(values.metadata || '{}');
      } catch {
        message.error('metadata JSON 格式错误');
        return;
      }
      setSaving(true);
      const data = {
        ...values,
        provider_id: provider?.id || 0,
        provider_name: provider?.name || 'qd-sd',
      };
      const res = modelEditMode && editingModel
        ? await adminAPI.updateModelConfig(editingModel.id, data)
        : await adminAPI.createModelConfig(data);
      if (res.code === 0) {
        message.success(modelEditMode ? '模型已更新' : '模型已创建');
        setModelModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('保存失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModel = async (model) => {
    try {
      const res = await adminAPI.deleteModelConfig(model.id);
      if (res.code === 0) {
        message.success('模型已删除');
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  const handleCreateDefaults = async () => {
    if (!provider) {
      message.warning('请先创建 QD 供应商');
      return;
    }
    setSaving(true);
    let created = 0;
    let skipped = 0;
    for (const m of DEFAULT_MODELS) {
      if (allModels.some(x => x.model_name === m.model_name)) {
        skipped++;
        continue;
      }
      try {
        const res = await adminAPI.createModelConfig({
          ...m,
          provider_id: provider.id,
          provider_name: provider.name,
        });
        if (res.code === 0) created++;
      } catch { /* 单个失败不影响其他模型 */ }
    }
    setSaving(false);
    message.success(`创建 ${created} 个模型` + (skipped ? `，跳过 ${skipped} 个已存在` : ''));
    fetchData();
  };

  const handleLookupUsage = async () => {
    const id = usageTaskId.trim();
    if (!id) { message.warning('请输入 QD 任务 ID'); return; }
    setUsageLoading(true);
    setTaskUsage(null);
    try {
      const res = await adminAPI.getQdTaskUsage(id);
      if (res.code === 0) setTaskUsage(res.data);
      else message.error(res.message || '查询失败');
    } catch (e) {
      message.error('查询失败: ' + (e.message || ''));
    } finally {
      setUsageLoading(false);
    }
  };

  const modelColumns = [
    {
      title: '模型',
      dataIndex: 'model_name',
      width: 240,
      render: (name, record) => {
        const meta = parseModelMetadata(record);
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Text strong>{record.display_name || name}</Text>
              {meta?.tier && <Tag color={meta.tierColor} style={{ fontSize: 11 }}>{meta.tier}</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>{name}</Text>
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v) => <Tag color={v === 'active' ? 'green' : 'red'}>{v === 'active' ? '启用' : '下架'}</Tag>,
    },
    {
      title: '价格',
      width: 220,
      render: (_, r) => {
        const rp = parseModelMetadata(r)?.resolutionPricing;
        return (
          <span style={{ fontWeight: 600, color: '#fa8c16' }}>
            {rp
              ? Object.entries(rp).map(([resolution, price]) => `${resolution} $${price}`).join(' / ')
              : '-'} <small style={{ color: '#888', fontWeight: 400 }}>/1M tokens</small>
          </span>
        );
      },
    },
    {
      title: '上游模型',
      dataIndex: 'upstream_model_name',
      width: 240,
      render: (v) => <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{v || '-'}</Text>,
    },
    {
      title: '特性',
      width: 220,
      render: (_, record) => {
        const tags = parseModelMetadata(record)?.features || [];
        return tags.length ? (
          <Space size={2} wrap>{tags.slice(0, 3).map((f, i) => <Tag key={i} style={{ fontSize: 11 }}>{f}</Tag>)}</Space>
        ) : <Text type="secondary">未配置</Text>;
      },
    },
    {
      title: '操作',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModel(record)} />
          <Popconfirm title="确定删除此模型？" onConfirm={() => handleDeleteModel(record)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 24px 24px', maxWidth: 1400 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <Space align="center" size={12}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #722ed1 0%, #13c2c2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ApiOutlined style={{ fontSize: 20, color: '#fff' }} />
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>QD 渠道配置</Title>
            <Space size={4}>
              {provider
                ? <Tag color="green" icon={<CheckCircleOutlined />}>已接入</Tag>
                : <Tag color="orange" icon={<CloseCircleOutlined />}>未接入</Tag>}
              {models.length > 0 && <Tag color="blue">{models.length} 个模型</Tag>}
            </Space>
          </div>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>刷新</Button>
      </div>

      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="QD 官转 Seedance"
        description="直接传素材 URL，不支持素材库、尾帧输出和智能多帧。模型价格按国际版 USD/1M tokens 配置，最终按平台 USD 结算；供应商、模型配置和上游任务查询都在本页完成，平台折扣跟随 API Key 设置。"
        style={{ marginBottom: 20 }}
      />

      {dashboard?.month_usage_error && (
        <Alert type="warning" showIcon message="上游用量查询失败" description={dashboard.month_usage_error} style={{ marginBottom: 20 }} />
      )}

      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="模型数量" value={dashboard?.model_count ?? models.length} suffix="个" />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="本月上游消耗"
              value={dashboard?.month_usage_usd ?? 0}
              precision={4}
              prefix="$"
              suffix={dashboard?.month_usage_usd != null ? 'USD' : '未查询'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="最近平台任务" value={dashboard?.tasks?.length ?? 0} suffix="条" />
          </Card>
        </Col>
      </Row>

      <Card
        title={<Space><KeyOutlined style={{ color: '#722ed1' }} /><span>供应商配置</span></Space>}
        extra={
          provider ? (
            <Space>
              <Button size="small" icon={<EditOutlined />} onClick={openEditProvider}>编辑</Button>
              <Popconfirm title="确定删除此供应商？关联模型将不可用。" onConfirm={handleDeleteProvider} okText="删除" cancelText="取消">
                <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>
            </Space>
          ) : (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateProvider}>创建供应商</Button>
          )
        }
        style={{ marginBottom: 20 }}
      >
        {provider ? (
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
            <Descriptions.Item label="供应商名称"><Text code>{provider.name}</Text></Descriptions.Item>
            <Descriptions.Item label="显示名称">{provider.display_name}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={provider.status === 'active' ? 'green' : 'red'}>{provider.status === 'active' ? '启用' : '禁用'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="API 地址"><Space><LinkOutlined /><Text code>已配置（编辑时可见）</Text></Space></Descriptions.Item>
            <Descriptions.Item label="API Key" span={2}>
              <Text code>{provider.api_key ? provider.api_key.substring(0, 10) + '****' + provider.api_key.slice(-4) : '未配置'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="本月上游消耗">
              <Text strong style={{ color: '#fa8c16' }}>
                {dashboard?.month_usage_usd != null ? `$${Number(dashboard.month_usage_usd).toFixed(4)} USD` : '-'}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>尚未配置 QD 供应商。</Paragraph>
            <Text type="secondary" style={{ fontSize: 12 }}>API Base：编辑时填写</Text>
          </div>
        )}
      </Card>

      <Card
        title={<Space><VideoCameraOutlined style={{ color: '#722ed1' }} /><span>Seedance 视频模型</span><Tag color="blue">{models.length}</Tag></Space>}
        extra={
          <Space>
            <Button size="small" disabled={!provider} onClick={handleCreateDefaults} loading={saving}>一键创建默认模型</Button>
            <Button type="primary" size="small" icon={<PlusOutlined />} disabled={!provider} onClick={openCreateModel}>添加模型</Button>
          </Space>
        }
        style={{ marginBottom: 20 }}
      >
        <Table
          columns={modelColumns}
          dataSource={models}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          scroll={{ x: 1100 }}
          locale={{ emptyText: '暂无模型，可点击「一键创建默认模型」' }}
        />
      </Card>

      <Card
        title={<Space><BarChartOutlined style={{ color: '#722ed1' }} /><span>上游任务用量查询</span></Space>}
        style={{ marginBottom: 20 }}
      >
        <Space.Compact style={{ width: '100%', maxWidth: 620, marginBottom: 16 }}>
          <Input
            value={usageTaskId}
            onChange={e => setUsageTaskId(e.target.value)}
            placeholder="输入 QD 任务 ID，例如 task_... 或 cgt-..."
            onPressEnter={handleLookupUsage}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={usageLoading} onClick={handleLookupUsage}>查询</Button>
        </Space.Compact>

        {taskUsage ? (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
            <Descriptions.Item label="任务 ID"><Text code>{taskUsage.task_id || taskUsage.id}</Text></Descriptions.Item>
            <Descriptions.Item label="模型">{taskUsage.model || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">{taskUsage.status || '-'}</Descriptions.Item>
            <Descriptions.Item label="分辨率">{taskUsage.resolution || '-'}</Descriptions.Item>
            <Descriptions.Item label="时长">{taskUsage.duration ? `${taskUsage.duration}s` : '-'}</Descriptions.Item>
            <Descriptions.Item label="进度">{taskUsage.progress != null ? `${taskUsage.progress}%` : '-'}</Descriptions.Item>
            <Descriptions.Item label="Tokens">{taskUsage.usage?.completion_tokens ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="上游费用">
              {qdUpstreamFee(taskUsage.billing)}
            </Descriptions.Item>
            <Descriptions.Item label="结算状态">{taskUsage.billing?.settled ? '已结算' : '未结算'}</Descriptions.Item>
            <Descriptions.Item label="视频地址" span={3}>
              {taskUsage.video_url
                ? <Text code copyable={{ text: taskUsage.video_url }} ellipsis style={{ maxWidth: 900 }}>{taskUsage.video_url}</Text>
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
            输入上游返回的 task_... 或 cgt-... 任务 ID 查询用量和费用；查询失败不代表视频生成失败。
          </Text>
        )}
      </Card>

      <Card title="最近 QD 平台任务" style={{ marginBottom: 20 }}>
        <Table
          size="small"
          pagination={false}
          rowKey="task_id"
          dataSource={dashboard?.tasks || []}
          locale={{ emptyText: '暂无任务' }}
          columns={[
            { title: '任务 ID', dataIndex: 'task_id', ellipsis: true, render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
            { title: '模型', dataIndex: 'model', width: 220 },
            { title: '状态', dataIndex: 'status', width: 100, render: v => <Tag color={v === 'completed' ? 'green' : v === 'failed' ? 'red' : 'blue'}>{v || '-'}</Tag> },
            { title: '平台扣费', dataIndex: 'billed_amount', width: 120, render: v => `$${Number(v || 0).toFixed(4)}` },
            { title: '时间', dataIndex: 'created_at', width: 180, render: v => v ? new Date(v).toLocaleString() : '-' },
            { title: '错误', dataIndex: 'error', ellipsis: true, render: v => v ? <Text type="danger" style={{ fontSize: 11 }}>{v}</Text> : '-' },
          ]}
        />
      </Card>

      <Modal
        title={providerEditMode ? '编辑 QD 供应商' : '创建 QD 供应商'}
        open={providerModalOpen}
        onCancel={() => setProviderModalOpen(false)}
        onOk={handleProviderSave}
        confirmLoading={saving}
        width={560}
        destroyOnHidden
      >
        <Form form={providerForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="供应商名称" rules={[{ required: true, message: '请输入供应商名称' }]}>
            <Input placeholder="qd-sd" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="qd" />
          </Form.Item>
          <Form.Item name="api_base" label="API Base URL" rules={[{ required: true, message: '请输入 API 地址' }]}>
            <Input placeholder="https://api.example.com/v1" />
          </Form.Item>
          <Form.Item name="api_key" label="API Key" rules={[{ required: true, message: '请输入 API Key' }]}>
            <Input.Password placeholder="sk-..." />
          </Form.Item>
          <Form.Item name="adapter_type" label="适配器类型">
            <Select options={[{ value: 'openai', label: 'OpenAI 兼容' }, { value: 'custom', label: '自定义' }]} />
          </Form.Item>
          <Form.Item name="provider_type" label="供应商类型">
            <Select options={[{ value: 'internal', label: '内部供应商' }, { value: 'external', label: '外部供应商' }]} />
          </Form.Item>
          <Space size={16}>
            <Form.Item name="priority" label="优先级">
              <InputNumber min={0} max={100} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select style={{ width: 120 }} options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '禁用' }]} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={modelEditMode ? '编辑 QD 模型' : '添加 QD 模型'}
        open={modelModalOpen}
        onCancel={() => setModelModalOpen(false)}
        onOk={handleModelSave}
        confirmLoading={saving}
        width={680}
        destroyOnHidden
      >
        <Form form={modelForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="model_name" label="公开模型名" rules={[{ required: true, message: '请输入模型名' }]}>
            <Input placeholder="qd/seedance-2.0" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="Seedance 2.0" />
          </Form.Item>
          <Form.Item name="upstream_model_name" label="上游模型名" rules={[{ required: true, message: '请输入上游模型名' }]}>
            <Input placeholder="dreamina-seedance-2-0-260128" />
          </Form.Item>
          <Form.Item name="api_path" label="上游创建路径">
            <Input placeholder="/videos" />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="input_price" label="基准单价（/1M tokens，720p）" rules={[{ required: true }]}>
              <InputNumber min={0} step={0.01} style={{ width: 220 }} />
            </Form.Item>
            <Form.Item name="priority" label="优先级">
              <InputNumber min={0} max={100} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select style={{ width: 120 }} options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '下架' }]} />
            </Form.Item>
          </Space>
          <Form.Item
            name="metadata"
            label="模型特性 / 分辨率 / 定价 JSON"
            extra="resolution_pricing 是实际计费价（$/1M tokens）；含参考视频用 resolution_pricing_with_video。"
          >
            <Input.TextArea rows={12} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>
          <Form.Item name="model_type" hidden><Input /></Form.Item>
          <Form.Item name="provider_id" hidden><InputNumber /></Form.Item>
          <Form.Item name="billing_type" hidden><Input /></Form.Item>
          <Form.Item name="output_price" hidden><InputNumber /></Form.Item>
          <Form.Item name="billing_rate" hidden><InputNumber /></Form.Item>
        </Form>
      </Modal>

      <Divider />
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        默认模型包含 qd/seedance-2.0 / 2.0-fast / 2.0-mini / 2.5。上游不提供素材库、尾帧输出和智能多帧；素材库 asset:// 会在网关侧拒绝。
      </Paragraph>
    </div>
  );
};

export default Qd;
